import { useFieldArray, useFormContext } from "react-hook-form";

import { Label, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import { LikertItemsSection } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { getNestedError } from "@components/AssessmentFormRenderer/utils/getNestedError";

interface FormLikertItemsProps {
  section: LikertItemsSection;
}

export const FormLikertItems: React.FC<FormLikertItemsProps> = ({ section }) => {
  const { control, watch, setValue, formState } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: section.source,
  });

  if (!fields.length) {
    return null;
  }

  if (!section.editable) {
    return (
      <div className="flex flex-col gap-2">
        <Label displayType="loud">{section.label}</Label>
        {fields.map((field, index) => {
          const itemPath = `${section.source}.${index}`;
          const name = watch(`${itemPath}.${section.nameKey}`);
          const value = watch(`${itemPath}.${section.valueKey}`);

          return (
            <div key={field.id} className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-700">{name}</span>
              <span className="text-sm text-gray-700">{value ?? "—"}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label displayType="loud">{section.label}</Label>
      {fields.map((field, index) => {
        const itemPath = `${section.source}.${index}`;
        const valueFieldPath = `${itemPath}.${section.valueKey}`;
        const name = watch(`${itemPath}.${section.nameKey}`);
        const currentValue = watch(valueFieldPath);
        const error = getNestedError(formState.errors, valueFieldPath);

        const selectItems: SelectItem[] = section.options.map((opt, optIndex) => ({
          primary: opt,
          id: String(optIndex),
        }));

        const selectedItem = selectItems.find((item) => item.primary === currentValue);

        const handleSelectChange = (item: SelectItem) => {
          setValue(valueFieldPath, item.primary, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        };

        return (
          <div key={field.id} className="flex flex-col gap-1">
            <span className="text-sm">{name}</span>
            <Select
              title=""
              items={selectItems}
              selected={selectedItem}
              setSelected={handleSelectChange}
              placeholderText="—"
              errorMessage={error && !Array.isArray(error) ? error.message?.toString() : undefined}
              formNoValidate={!error}
            />
          </div>
        );
      })}
    </div>
  );
};
